// @turf/rhumb-distance と @turf/rhumb-bearing ライブラリから必要な関数をインポート
import getDistance from '@turf/rhumb-distance';
import getBearing from '@turf/rhumb-bearing';

// TripBuilder クラスを定義します
export default class TripBuilder {
    // コンストラクタを定義し、初期設定を行います
    constructor({
        waypoints, // 経路点の配列
        speed = 10, // 移動速度のデフォルト値（メートル単位）
        turnSpeed = 45, // 旋回速度のデフォルト値（度単位）
        loop = false // ループ動作の有無
    }) {
        this.keyframes = []; // キーフレームを格納する配列
        this.speed = speed; // 移動速度を設定
        this.turnSpeed = turnSpeed; // 旋回速度を設定
        this.loop = loop; // ループ動作の設定
        this.totalTime = 0; // 総時間の初期化

        // 経路点を走査し、移動動作を追加します
        for (const p of waypoints) {
            this._moveTo(p);
        }
        // ループが有効かつ経路点が2点以上ある場合、始点への移動と向きの変更を追加します
        if (loop && waypoints.length > 2) {
            this._moveTo(waypoints[0]);
            this._turnTo(this.keyframes[0].heading);
        }
    }

    // 指定した点への移動処理を行うプライベートメソッド
    _moveTo(point) {
        // 最初のキーフレームの場合は、初期位置を追加します
        if (this.keyframes.length === 0) {
            this.keyframes.push({
                point,
                time: 0
            });
            return;
        }

        // 前のキーフレームを取得し、距離と方位角を計算します
        const prevKeyframe = this.keyframes[this.keyframes.length - 1];
        const distance = getDistance(prevKeyframe.point, point, { units: 'kilometers' }) * 1000;
        const heading = getBearing(prevKeyframe.point, point);

        // 距離が非常に短い場合は何もしません
        if (distance < 0.1) {
            return;
        }
        // 前のキーフレームの向きが未定義の場合は、現在の方位角を設定します
        if (prevKeyframe.heading === undefined) {
            prevKeyframe.heading = heading;
        } else {
            // そうでない場合は旋回動作を追加します
            this._turnTo(heading);
        }

        // 移動にかかる時間を計算し、キーフレームを追加します
        const duration = distance / this.speed;

        this.keyframes.push({
            point,
            heading,
            time: (this.totalTime += duration)
        });
    }

    // 指定した方位角への旋回処理を行うプライベートメソッド
    _turnTo(heading) {
        const prevKeyframe = this.keyframes[this.keyframes.length - 1];
        const angle = Math.abs(getTurnAngle(prevKeyframe.heading, heading));

        // 旋回角が0より大きい場合は旋回動作を追加します
        if (angle > 0) {
            const duration = angle / this.turnSpeed;
            this.keyframes.push({
                point: prevKeyframe.point,
                heading,
                time: (this.totalTime += duration)
            });
        }
    }

    // 指定したタイムスタンプでのキーフレームを取得するメソッド
    getFrame(timestamp) {
        // ループ動作が有効の場合はタイムスタンプを総時間で割った余りを、
        // そうでない場合はタイムスタンプと総時間の小さい方を使用します
        timestamp = this.loop ? timestamp % this.totalTime : Math.min(timestamp, this.totalTime);
        // 指定したタイムスタンプに最も近いキーフレームを検索します
        const i = this.keyframes.findIndex(s => s.time >= timestamp);
        const startState = this.keyframes[i - 1];
        const endState = this.keyframes[i];
        const r = (timestamp - startState.time) / (endState.time - startState.time);

        // 線形補間を行い、その時点での位置と向きを計算して返します
        return {
            point: [
                startState.point[0] * (1 - r) + endState.point[0] * r,
                startState.point[1] * (1 - r) + endState.point[1] * r
            ],
            heading: startState.heading + getTurnAngle(startState.heading, endState.heading) * r
        };
    }
}

// 2点間の旋回角を計算する関数
function getTurnAngle(startHeading, endHeading) {
    let turnAngle = endHeading - startHeading;
    // 旋回角を-180度から180度の範囲に正規化します
    if (turnAngle < -180) turnAngle += 360;
    if (turnAngle > 180) turnAngle -= 360;
    return turnAngle;
}